import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import { MapPin, Plus, Edit, Trash2, Search, Building2, Box, Gauge, RefreshCw, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, Landmark, Link2, Unlink, Shield, ShieldCheck, Eye, EyeOff, Save, X, BanknoteIcon } from 'lucide-react';

const Locations = () => {
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // CSV Upload states
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  // Legal Entity states
  const [showLegalEntityDrawer, setShowLegalEntityDrawer] = useState(false);
  const [legalEntityLocation, setLegalEntityLocation] = useState(null);
  const [legalEntities, setLegalEntities] = useState([]);
  const [allLegalEntities, setAllLegalEntities] = useState([]);
  const [leLoading, setLeLoading] = useState(false);
  const [leSyncing, setLeSyncing] = useState(false);
  const [showAchDialog, setShowAchDialog] = useState(false);
  const [achEntity, setAchEntity] = useState(null);
  const [achForm, setAchForm] = useState({ bank_name: '', routing_number: '', account_number: '', account_type: 'checking' });
  const [achSaving, setAchSaving] = useState(false);
  const [achRevealed, setAchRevealed] = useState(null);
  const [achLoading, setAchLoading] = useState(false);
  const [showLinkDropdown, setShowLinkDropdown] = useState(false);
  const [showAddLegalEntityDialog, setShowAddLegalEntityDialog] = useState(false);
  const [newLeForm, setNewLeForm] = useState({ legal_entity_name: '', ein: '' });
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    pms_property_code: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA',
    property_type: 'residential',
    square_footage: '',
    year_built: '',
    timezone: 'America/New_York',
    bp_customer_id: '',
    bp_property_id: '',
    status: 'active',
    short_name: '',
    lat: '',
    lng: '',
    valid_from: '',
    valid_to: '',
    legal_entity_name: '',
    ein: '',
  });

  useEffect(() => {
    const initializePage = async () => {
      try {
        // Fetch fresh account data
        const { data: accounts } = await api.get('/auth/accounts');
        const storedAccountStr = localStorage.getItem('current_account');
        const storedAccount = storedAccountStr ? JSON.parse(storedAccountStr) : null;
        
        // Find the current account with fresh data
        let account = null;
        if (storedAccount) {
          account = accounts.find(a => a.id === storedAccount.id);
        }
        if (!account && accounts.length > 0) {
          account = accounts[0];
        }
        
        if (!account) {
          navigate('/dashboard');
          navigate('/dashboard');
          return;
        }
        
        // Update localStorage with fresh data
        localStorage.setItem('current_account', JSON.stringify(account));
        
        // Check if UBM is enabled
        if (!account.enabled_apps?.ubm) {
          navigate('/dashboard');
          return;
        }
        
        setCurrentAccount(account);
        setInitializing(false);
      } catch (error) {
        console.error('Failed to initialize:', error);
        navigate('/dashboard');
      }
    };
    
    initializePage();
  }, [navigate]);

  useEffect(() => {
    if (currentAccount && !initializing) {
      fetchLocations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount, initializing]);

  const fetchLocations = async () => {
    if (!currentAccount) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/locations`);
      setLocations(data || []);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to load locations:', error);
      }
      setLocations([]);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      name: '',
      pms_property_code: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA',
      property_type: 'residential',
      square_footage: '',
      year_built: '',
      timezone: 'America/New_York',
      bp_customer_id: '',
      bp_property_id: '',
      status: 'active',
      short_name: '',
      lat: '',
      lng: '',
      valid_from: '',
      valid_to: '',
      legal_entity_name: '',
      ein: '',
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.city || !form.state || !form.zip) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/ubm/accounts/${currentAccount.id}/locations`, form);
      toast.success('Property added successfully');
      setShowAddModal(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add property');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedLocation) return;
    
    setSaving(true);
    try {
      await api.put(`/ubm/accounts/${currentAccount.id}/locations/${selectedLocation.id}`, form);
      toast.success('Property updated successfully');
      setShowEditModal(false);
      setSelectedLocation(null);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update property');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLocation) return;
    
    try {
      await api.delete(`/ubm/accounts/${currentAccount.id}/locations/${selectedLocation.id}`);
      toast.success('Property deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete property');
    }
  };

  const openEditModal = (location) => {
    setSelectedLocation(location);
    setForm({
      name: location.name || '',
      pms_property_code: location.pms_property_code || '',
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zip: location.zip || '',
      country: location.country || 'USA',
      property_type: location.property_type || 'residential',
      square_footage: location.square_footage || '',
      year_built: location.year_built || '',
      timezone: location.timezone || 'America/New_York',
      bp_customer_id: location.bp_customer_id || '',
      bp_property_id: location.bp_property_id || '',
      status: location.status || 'active',
      short_name: location.short_name || '',
      lat: location.lat != null ? String(location.lat) : '',
      lng: location.lng != null ? String(location.lng) : '',
      valid_from: location.valid_from ? String(location.valid_from).split('T')[0] : '',
      valid_to: location.valid_to ? String(location.valid_to).split('T')[0] : '',
      legal_entity_name: location.legal_entity_name || '',
      ein: location.ein || '',
    });
    setShowEditModal(true);
  };

  const openDeleteConfirm = (location) => {
    setSelectedLocation(location);
    setShowDeleteConfirm(true);
  };

  // CSV Upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/ubm/accounts/${currentAccount.id}/locations/csv-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'locations_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await api.post(`/ubm/accounts/${currentAccount.id}/locations/csv-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResults(data);
      
      if (data.created > 0 || data.updated > 0) {
        toast.success(`Created ${data.created}, Updated ${data.updated} properties`);
        fetchLocations();
      }
      
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
      setUploadResults({ errors: [{ error: error.response?.data?.detail || 'Upload failed' }] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Filter locations
  const filteredLocations = locations.filter(loc => {
    const matchesSearch = !searchQuery || 
      loc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      loc.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      loc.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      loc.pms_property_code?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = typeFilter === 'all' || loc.property_type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  // Legal Entity helpers
  const fetchLegalEntities = async () => {
    if (!currentAccount) return;
    setLeLoading(true);
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/legal-entities`);
      const entities = data.entities || data || [];
      setAllLegalEntities(entities);
      return entities;
    } catch { setAllLegalEntities([]); return []; }
    finally { setLeLoading(false); }
  };

  const openLegalEntityDrawer = async (location) => {
    setLegalEntityLocation(location);
    setShowLegalEntityDrawer(true);
    setLeLoading(true);
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/legal-entities`);
      const entities = data.entities || data || [];
      setAllLegalEntities(entities);
      setLegalEntities(entities.filter(le => le.linked_location_id === location.id));
    } catch { setAllLegalEntities([]); setLegalEntities([]); }
    finally { setLeLoading(false); }
  };

  const handleSyncLegalEntities = async () => {
    if (!currentAccount) return;
    setLeSyncing(true);
    try {
      const accountDoc = await api.get('/auth/accounts');
      const acct = (accountDoc.data || []).find(a => a.id === currentAccount.id);
      const customerId = acct?.bp_customer_unique_id || acct?.bp_customer_id || '';
      
      const { data } = await api.post('/ubm/sync/legal-entities', {
        account_id: currentAccount.id,
        customer_unique_id: customerId,
      });
      toast.success(`Synced ${data.synced} legal entities (${data.auto_linked} auto-linked)`);
      // Re-fetch
      const fresh = await fetchLegalEntities();
      if (legalEntityLocation) {
        setLegalEntities((fresh || []).filter(le => le.linked_location_id === legalEntityLocation.id));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync legal entities');
    }
    finally { setLeSyncing(false); }
  };

  const handleLinkEntity = async (entityId) => {
    if (!legalEntityLocation) return;
    try {
      await api.put(`/ubm/accounts/${currentAccount.id}/legal-entities/${entityId}`, {
        linked_location_id: legalEntityLocation.id,
      });
      toast.success('Legal entity linked to property');
      const fresh = await fetchLegalEntities();
      setLegalEntities((fresh || []).filter(le => le.linked_location_id === legalEntityLocation.id));
      setShowLinkDropdown(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link entity');
    }
  };

  const handleUnlinkEntity = async (entityId) => {
    try {
      await api.put(`/ubm/accounts/${currentAccount.id}/legal-entities/${entityId}`, {
        linked_location_id: null,
      });
      toast.success('Legal entity unlinked');
      const fresh = await fetchLegalEntities();
      setLegalEntities((fresh || []).filter(le => le.linked_location_id === legalEntityLocation.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink entity');
    }
  };

  const handleSaveAch = async () => {
    if (!achEntity || !achForm.routing_number || !achForm.account_number || !achForm.bank_name) {
      toast.error('Bank name, routing number, and account number are required');
      return;
    }
    setAchSaving(true);
    try {
      await api.put(`/ubm/accounts/${currentAccount.id}/legal-entities/${achEntity.id}/ach`, achForm);
      toast.success('ACH details saved securely');
      setShowAchDialog(false);
      setAchEntity(null);
      const fresh = await fetchLegalEntities();
      setLegalEntities((fresh || []).filter(le => le.linked_location_id === legalEntityLocation.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save ACH details');
    }
    finally { setAchSaving(false); }
  };

  const handleRevealAch = async (entity) => {
    if (achRevealed?.id === entity.id) { setAchRevealed(null); return; }
    setAchLoading(true);
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/legal-entities/${entity.id}/ach`);
      setAchRevealed({ id: entity.id, details: data.ach_details || {} });
    } catch { toast.error('Failed to load ACH details'); }
    finally { setAchLoading(false); }
  };

  const handleAddLegalEntity = async () => {
    if (!newLeForm.legal_entity_name) { toast.error('Name is required'); return; }
    try {
      await api.post(`/ubm/accounts/${currentAccount.id}/legal-entities`, {
        ...newLeForm,
        linked_location_id: legalEntityLocation?.id || null,
      });
      toast.success('Legal entity created');
      setShowAddLegalEntityDialog(false);
      setNewLeForm({ legal_entity_name: '', ein: '' });
      const fresh = await fetchLegalEntities();
      if (legalEntityLocation) {
        setLegalEntities((fresh || []).filter(le => le.linked_location_id === legalEntityLocation.id));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create legal entity');
    }
  };

  const unlinkedEntities = allLegalEntities.filter(le => !le.linked_location_id);

  const propertyTypes = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'mixed', label: 'Mixed Use' },
  ];

  const timezones = [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
    { value: 'America/Phoenix', label: 'Arizona (AZ)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
  ];

  const handleExportCSV = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/locations/export`, { responseType: 'text' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `properties_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Properties</h1>
              <p className="text-sm text-muted-foreground">
                Manage properties and buildings for utility bill tracking
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="rounded-none"
              data-testid="export-csv-btn"
              disabled={locations.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(true)}
              className="rounded-none"
              data-testid="upload-csv-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload CSV
            </Button>
            <Button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="rounded-none"
              data-testid="add-location-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Property
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="border border-border rounded-none p-4 bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Building2 className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide font-medium">Total Properties</span>
            </div>
            <p className="text-3xl font-black tracking-tighter">{locations.length}</p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Box className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide font-medium">Total Spaces</span>
            </div>
            <p className="text-3xl font-black tracking-tighter">
              {locations.reduce((sum, loc) => sum + (loc.space_count || 0), 0)}
            </p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Gauge className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide font-medium">Total Meters</span>
            </div>
            <p className="text-3xl font-black tracking-tighter">
              {locations.reduce((sum, loc) => sum + (loc.meter_count || 0), 0)}
            </p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <MapPin className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide font-medium">Active</span>
            </div>
            <p className="text-3xl font-black tracking-tighter">
              {locations.filter(l => l.status === 'active').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search locations..."
                className="rounded-none pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="w-40">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="rounded-none">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {propertyTypes.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={fetchLocations}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Table */}
        <div className="border border-border rounded-none bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Property</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Address</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Valid Period</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Type</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Legal Entity</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">EIN</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">BP Customer</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Spaces</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Meters</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Accounts</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Status</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {initializing || loading ? (
                  <tr>
                    <td colSpan={12} className="text-center p-8 text-muted-foreground">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      {initializing ? 'Initializing...' : 'Loading properties...'}
                    </td>
                  </tr>
                ) : filteredLocations.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center p-8 text-muted-foreground">
                      <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No properties found</p>
                      <p className="text-xs mt-1">Add your first property to get started</p>
                    </td>
                  </tr>
                ) : (
                  filteredLocations.map((location) => (
                    <tr key={location.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{location.name}</p>
                          {location.pms_property_code && (
                            <p className="text-xs text-muted-foreground font-mono">{location.pms_property_code}</p>
                          )}
                          {location.bp_property_id && (
                            <p className="text-xs text-muted-foreground font-mono mt-0.5">BP - {location.bp_property_id.substring(0, 12)}...</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-sm">
                          <p>{location.address}</p>
                          <p className="text-muted-foreground">{location.city}, {location.state} {location.zip}{location.country ? `, ${location.country}` : ''}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`location-valid-period-${location.id}`}>
                          {(location.valid_from || location.valid_to) ? (
                            <>
                              {location.valid_from ? new Date(location.valid_from).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'}) : '–'}
                              {' → '}
                              {location.valid_to ? new Date(location.valid_to).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'}) : 'Ongoing'}
                            </>
                          ) : '–'}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="rounded-none capitalize text-xs">
                          {location.property_type || 'N/A'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <span className="text-sm" data-testid={`location-legal-entity-${location.id}`}>{location.legal_entity_name || '-'}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-xs font-mono text-muted-foreground" data-testid={`location-ein-${location.id}`}>{location.ein || '-'}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-xs font-mono text-muted-foreground">{location.bp_customer_id || '-'}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-medium">{location.space_count || 0}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-medium">{location.meter_count || 0}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-medium" data-testid={`location-account-count-${location.id}`}>
                          {location.account_count || 0}
                        </span>
                      </td>
                      <td className="p-3">
                        <Badge 
                          className={`rounded-none text-xs ${
                            location.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {location.status || 'active'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-none"
                            onClick={() => openEditModal(location)}
                            title="Edit"
                            data-testid={`edit-location-${location.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-none text-destructive hover:text-destructive"
                            onClick={() => openDeleteConfirm(location)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {filteredLocations.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Showing {filteredLocations.length} of {locations.length} property(ies)
          </p>
        )}
      </div>

      {/* Add Property Side Panel */}
      <Sheet open={showAddModal} onOpenChange={setShowAddModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="add-property-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Add Property</SheetTitle>
            <SheetDescription>Add a new property or building to track utility bills.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleAdd} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Property Name *</Label>
                <Input
                  className="rounded-none mt-1"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Sunset Apartments"
                  required
                />
              </div>
              <div>
                <Label>PMS Property Code</Label>
                <Input
                  className="rounded-none mt-1 font-mono"
                  value={form.pms_property_code}
                  onChange={(e) => setForm({ ...form, pms_property_code: e.target.value })}
                  placeholder="e.g., PROP001"
                />
              </div>
              <div>
                <Label>Property Type</Label>
                <Select
                  value={form.property_type}
                  onValueChange={(value) => setForm({ ...form, property_type: value })}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Street Address *</Label>
                <Input
                  className="rounded-none mt-1"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="e.g., 123 Main Street"
                  required
                />
              </div>
              <div>
                <Label>City *</Label>
                <Input
                  className="rounded-none mt-1"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="e.g., Los Angeles"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>State *</Label>
                  <Input
                    className="rounded-none mt-1"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="CA"
                    maxLength={2}
                    required
                  />
                </div>
                <div>
                  <Label>ZIP *</Label>
                  <Input
                    className="rounded-none mt-1"
                    value={form.zip}
                    onChange={(e) => setForm({ ...form, zip: e.target.value })}
                    placeholder="90001"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  className="rounded-none mt-1"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  placeholder="USA"
                  data-testid="add-property-country"
                />
              </div>
              <div>
                <Label>Timezone</Label>
                <Select
                  value={form.timezone}
                  onValueChange={(value) => setForm({ ...form, timezone: value })}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timezones.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(value) => setForm({ ...form, status: value })}
                >
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-property-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Square Footage</Label>
                <Input
                  className="rounded-none mt-1"
                  type="number"
                  value={form.square_footage}
                  onChange={(e) => setForm({ ...form, square_footage: e.target.value })}
                  placeholder="e.g., 50000"
                />
              </div>
              <div>
                <Label>Year Built</Label>
                <Input
                  className="rounded-none mt-1"
                  type="number"
                  value={form.year_built}
                  onChange={(e) => setForm({ ...form, year_built: e.target.value })}
                  placeholder="e.g., 1985"
                  min="1800"
                  max={new Date().getFullYear()}
                />
              </div>
              <div>
                <Label>BP Customer ID</Label>
                <Input
                  className="rounded-none mt-1 font-mono"
                  value={form.bp_customer_id}
                  onChange={(e) => setForm({ ...form, bp_customer_id: e.target.value })}
                  placeholder="e.g., 320"
                />
              </div>
              <div>
                <Label>BP Property ID</Label>
                <Input
                  className="rounded-none mt-1 font-mono"
                  value={form.bp_property_id}
                  onChange={(e) => setForm({ ...form, bp_property_id: e.target.value })}
                  placeholder="Site UUID from BillPortal"
                />
              </div>
              <div className="col-span-2">
                <Label>Short Name</Label>
                <Input className="rounded-none mt-1" value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} placeholder="e.g., Sunset" data-testid="add-property-short-name" />
              </div>
              <div>
                <Label>Latitude</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="e.g., 34.0522" data-testid="add-property-lat" />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="e.g., -118.2437" data-testid="add-property-lng" />
              </div>
              <div>
                <Label>Valid From</Label>
                <Input type="date" className="rounded-none mt-1" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="add-property-valid-from" />
              </div>
              <div>
                <Label>Valid To</Label>
                <Input type="date" className="rounded-none mt-1" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} data-testid="add-property-valid-to" />
              </div>
              <div>
                <Label>Legal Entity</Label>
                <Input className="rounded-none mt-1" value={form.legal_entity_name} onChange={(e) => setForm({ ...form, legal_entity_name: e.target.value })} placeholder="e.g., Sunset Properties LLC" data-testid="add-property-legal-entity" />
              </div>
              <div>
                <Label>EIN</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} placeholder="XX-XXXXXXX" data-testid="add-property-ein" />
              </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-add-property-btn">
                {saving ? 'Adding...' : 'Add Property'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Edit Property Side Panel */}
      <Sheet open={showEditModal} onOpenChange={setShowEditModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="edit-property-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Edit Property</SheetTitle>
            <SheetDescription>Update property details.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleEdit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Property Name *</Label>
                <Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <Label>PMS Property Code</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.pms_property_code} onChange={(e) => setForm({ ...form, pms_property_code: e.target.value })} />
              </div>
              <div>
                <Label>Property Type</Label>
                <Select value={form.property_type} onValueChange={(value) => setForm({ ...form, property_type: value })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Street Address *</Label>
                <Input className="rounded-none mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
              </div>
              <div>
                <Label>City *</Label>
                <Input className="rounded-none mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>State *</Label>
                  <Input className="rounded-none mt-1" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} required />
                </div>
                <div>
                  <Label>ZIP *</Label>
                  <Input className="rounded-none mt-1" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} required />
                </div>
              </div>
              <div>
                <Label>Country</Label>
                <Input className="rounded-none mt-1" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="USA" data-testid="edit-property-country" />
              </div>
              <div>
                <Label>Timezone</Label>
                <Select value={form.timezone} onValueChange={(value) => setForm({ ...form, timezone: value })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {timezones.map(tz => (<SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-property-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Square Footage</Label>
                <Input className="rounded-none mt-1" type="number" value={form.square_footage} onChange={(e) => setForm({ ...form, square_footage: e.target.value })} />
              </div>
              <div>
                <Label>Year Built</Label>
                <Input className="rounded-none mt-1" type="number" value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })} min="1800" max={new Date().getFullYear()} />
              </div>
              <div>
                <Label>BP Customer ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.bp_customer_id} onChange={(e) => setForm({ ...form, bp_customer_id: e.target.value })} />
              </div>
              <div>
                <Label>BP Property ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.bp_property_id} onChange={(e) => setForm({ ...form, bp_property_id: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Short Name</Label>
                <Input className="rounded-none mt-1" value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} data-testid="edit-property-short-name" />
              </div>
              <div>
                <Label>Latitude</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="edit-property-lat" />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="edit-property-lng" />
              </div>
              <div>
                <Label>Valid From</Label>
                <Input type="date" className="rounded-none mt-1" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="edit-property-valid-from" />
              </div>
              <div>
                <Label>Valid To</Label>
                <Input type="date" className="rounded-none mt-1" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} data-testid="edit-property-valid-to" />
              </div>
              <div>
                <Label>Legal Entity</Label>
                <Input className="rounded-none mt-1" value={form.legal_entity_name} onChange={(e) => setForm({ ...form, legal_entity_name: e.target.value })} data-testid="edit-property-legal-entity" />
              </div>
              <div>
                <Label>EIN</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} placeholder="XX-XXXXXXX" data-testid="edit-property-ein" />
              </div>
              <div className="col-span-2">
                <Button type="button" variant="outline" className="rounded-none w-full" data-testid="edit-property-manage-legal-entities"
                  onClick={() => { setShowEditModal(false); openLegalEntityDrawer(selectedLocation); }}>
                  <Landmark className="h-4 w-4 mr-2" /> Manage Legal Entities
                </Button>
              </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-edit-property-btn">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Delete Property</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedLocation?.name}"? 
              This will also delete all associated spaces and meters. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" className="rounded-none" onClick={handleDelete}>
              Delete Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={showUploadModal} onOpenChange={(open) => { setShowUploadModal(open); if (!open) setUploadResults(null); }}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Upload Properties CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk create or update properties. Existing properties will be matched by PMS Property Code or Name + Address.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Download Template */}
            <div className="border border-dashed border-border rounded-none p-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Download Template</p>
                  <p className="text-xs text-muted-foreground">Get a CSV template with all required columns</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-none" onClick={handleDownloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Template
                </Button>
              </div>
            </div>

            {/* Upload Area */}
            <div className="border border-dashed border-border rounded-none p-6 text-center bg-muted/10 hover:bg-muted/20 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="hidden"
                id="csv-upload"
                data-testid="csv-file-input"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {uploading ? 'Uploading...' : 'Click to upload CSV file'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  CSV files only
                </p>
              </label>
            </div>

            {/* Upload Results */}
            {uploadResults && (
              <div className="space-y-2">
                {(uploadResults.created > 0 || uploadResults.updated > 0) && (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 p-2 rounded-none">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Created: {uploadResults.created}, Updated: {uploadResults.updated}</span>
                  </div>
                )}
                {uploadResults.errors?.length > 0 && (
                  <div className="bg-red-50 p-2 rounded-none">
                    <div className="flex items-center gap-2 text-red-600 mb-2">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">{uploadResults.errors.length} Error(s)</span>
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                      {uploadResults.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-red-700">
                          {err.row ? `Row ${err.row}: ` : ''}{err.error}
                        </p>
                      ))}
                      {uploadResults.errors.length > 5 && (
                        <p className="text-xs text-red-500 mt-1">
                          ...and {uploadResults.errors.length - 5} more errors
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => { setShowUploadModal(false); setUploadResults(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Legal Entity Drawer */}
      <Sheet open={showLegalEntityDrawer} onOpenChange={(open) => { setShowLegalEntityDrawer(open); if (!open) { setAchRevealed(null); setShowLinkDropdown(false); } }}>
        <SheetContent side="right" className="sm:max-w-[600px] w-[600px] flex flex-col p-0" data-testid="legal-entity-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-primary" />
              Legal Entities
            </SheetTitle>
            <SheetDescription>
              Manage legal entities linked to <strong>{legalEntityLocation?.name || 'this property'}</strong>
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="rounded-none text-xs"
                onClick={handleSyncLegalEntities}
                disabled={leSyncing}
                data-testid="sync-legal-entities-btn"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${leSyncing ? 'animate-spin' : ''}`} />
                {leSyncing ? 'Syncing from MySQL...' : 'Sync from MySQL'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-none text-xs"
                onClick={() => setShowLinkDropdown(!showLinkDropdown)}
                data-testid="link-entity-btn"
              >
                <Link2 className="h-3 w-3 mr-1" />
                Link Existing
              </Button>
              <Button
                size="sm"
                className="rounded-none text-xs"
                onClick={() => { setNewLeForm({ legal_entity_name: '', ein: '' }); setShowAddLegalEntityDialog(true); }}
                data-testid="add-legal-entity-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add New
              </Button>
            </div>

            {/* Link Existing Dropdown */}
            {showLinkDropdown && (
              <div className="border border-border rounded-none bg-muted/20 p-3" data-testid="link-entity-dropdown">
                <p className="text-xs font-medium mb-2 text-muted-foreground">Unlinked Legal Entities ({unlinkedEntities.length})</p>
                {unlinkedEntities.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No unlinked entities available. Sync from MySQL or add manually.</p>
                ) : (
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {unlinkedEntities.map(le => (
                      <div key={le.id} className="flex items-center justify-between p-2 border border-border bg-background hover:bg-muted/30">
                        <div>
                          <p className="text-xs font-medium">{le.legal_entity_name}</p>
                          {le.ein && <p className="text-[10px] text-muted-foreground">EIN: {le.ein}</p>}
                          {le.site_name && <p className="text-[10px] text-muted-foreground">Site: {le.site_name}</p>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 rounded-none text-[10px]"
                          onClick={() => handleLinkEntity(le.id)}
                          data-testid={`link-le-${le.id}`}
                        >
                          <Link2 className="h-3 w-3 mr-1" />
                          Link
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Linked Legal Entities List */}
            {leLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                <RefreshCw className="h-4 w-4 animate-spin" /> Loading legal entities...
              </div>
            ) : legalEntities.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Landmark className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No legal entities linked to this property</p>
                <p className="text-xs mt-1">Sync from MySQL or link an existing entity</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Linked Entities ({legalEntities.length})
                </p>
                {legalEntities.map(le => (
                  <div key={le.id} className="border border-border rounded-none bg-card" data-testid={`legal-entity-card-${le.id}`}>
                    <div className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">{le.legal_entity_name}</p>
                            <Badge variant="outline" className="rounded-none text-[10px] shrink-0">
                              {le.sync_source === 'mysql' ? 'MySQL' : 'Manual'}
                            </Badge>
                          </div>
                          {le.ein && (
                            <p className="text-xs text-muted-foreground mt-0.5">EIN: {le.ein}</p>
                          )}
                          {le.site_name && (
                            <p className="text-[10px] text-muted-foreground">Site: {le.site_name}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 rounded-none"
                            onClick={() => handleUnlinkEntity(le.id)}
                            title="Unlink from property"
                            data-testid={`unlink-le-${le.id}`}
                          >
                            <Unlink className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                      </div>

                      {/* ACH Section */}
                      <div className="mt-3 pt-2 border-t border-border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {le.ach_details?.has_data ? (
                              <Badge className="rounded-none text-[10px] bg-green-100 text-green-800">
                                <ShieldCheck className="h-3 w-3 mr-1" /> ACH Configured
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="rounded-none text-[10px]">
                                <Shield className="h-3 w-3 mr-1" /> No ACH
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {le.ach_details?.has_data && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 rounded-none text-[10px]"
                                onClick={() => handleRevealAch(le)}
                                disabled={achLoading}
                                data-testid={`reveal-ach-${le.id}`}
                              >
                                {achRevealed?.id === le.id ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                                {achRevealed?.id === le.id ? 'Hide' : 'View'}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 rounded-none text-[10px]"
                              onClick={async () => {
                                setAchEntity(le);
                                if (le.ach_details?.has_data) {
                                  try {
                                    const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/legal-entities/${le.id}/ach`);
                                    const ach = data.ach_details || {};
                                    setAchForm({
                                      bank_name: ach.bank_name || '',
                                      routing_number: ach.routing_number || '',
                                      account_number: ach.account_number || '',
                                      account_type: ach.account_type || 'checking',
                                    });
                                  } catch {
                                    setAchForm({ bank_name: '', routing_number: '', account_number: '', account_type: 'checking' });
                                  }
                                } else {
                                  setAchForm({ bank_name: '', routing_number: '', account_number: '', account_type: 'checking' });
                                }
                                setShowAchDialog(true);
                              }}
                              data-testid={`edit-ach-${le.id}`}
                            >
                              <BanknoteIcon className="h-3 w-3 mr-1" />
                              {le.ach_details?.has_data ? 'Update ACH' : 'Add ACH'}
                            </Button>
                          </div>
                        </div>

                        {/* Revealed ACH details */}
                        {achRevealed?.id === le.id && achRevealed.details && (
                          <div className="mt-2 p-2 bg-muted/30 border border-border text-xs space-y-1" data-testid={`ach-details-${le.id}`}>
                            {achRevealed.details.bank_name && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Bank</span>
                                <span className="font-mono">{achRevealed.details.bank_name}</span>
                              </div>
                            )}
                            {achRevealed.details.routing_number && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Routing #</span>
                                <span className="font-mono">{achRevealed.details.routing_number}</span>
                              </div>
                            )}
                            {achRevealed.details.account_number && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Account #</span>
                                <span className="font-mono">{achRevealed.details.account_number}</span>
                              </div>
                            )}
                            {achRevealed.details.account_type && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Type</span>
                                <span className="capitalize">{achRevealed.details.account_type}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* ACH Configuration Dialog */}
      <Dialog open={showAchDialog} onOpenChange={setShowAchDialog}>
        <DialogContent className="rounded-none max-w-md" data-testid="ach-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              {achEntity?.ach_details?.has_data ? 'Update' : 'Configure'} ACH Details
            </DialogTitle>
            <DialogDescription>
              Banking details for <strong>{achEntity?.legal_entity_name}</strong>. Data is encrypted at rest using Fernet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Bank Name *</Label>
              <Input
                className="rounded-none mt-1"
                value={achForm.bank_name}
                onChange={(e) => setAchForm({ ...achForm, bank_name: e.target.value })}
                placeholder="e.g., JPMorgan Chase"
                data-testid="ach-bank-name"
              />
            </div>
            <div>
              <Label>Routing Number *</Label>
              <Input
                className="rounded-none mt-1 font-mono"
                value={achForm.routing_number}
                onChange={(e) => setAchForm({ ...achForm, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                placeholder="9-digit routing number"
                maxLength={9}
                data-testid="ach-routing"
              />
            </div>
            <div>
              <Label>Account Number *</Label>
              <Input
                className="rounded-none mt-1 font-mono"
                value={achForm.account_number}
                onChange={(e) => setAchForm({ ...achForm, account_number: e.target.value.replace(/\D/g, '') })}
                placeholder="Account number"
                data-testid="ach-account"
              />
            </div>
            <div>
              <Label>Account Type</Label>
              <Select value={achForm.account_type} onValueChange={(v) => setAchForm({ ...achForm, account_type: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="ach-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="checking">Checking</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowAchDialog(false)}>
              Cancel
            </Button>
            <Button className="rounded-none" onClick={handleSaveAch} disabled={achSaving} data-testid="save-ach-btn">
              {achSaving ? 'Encrypting & Saving...' : 'Save ACH Details'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Legal Entity Dialog */}
      <Dialog open={showAddLegalEntityDialog} onOpenChange={setShowAddLegalEntityDialog}>
        <DialogContent className="rounded-none max-w-md" data-testid="add-legal-entity-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Legal Entity
            </DialogTitle>
            <DialogDescription>
              Create a new legal entity and link it to <strong>{legalEntityLocation?.name || 'this property'}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Legal Entity Name *</Label>
              <Input
                className="rounded-none mt-1"
                value={newLeForm.legal_entity_name}
                onChange={(e) => setNewLeForm({ ...newLeForm, legal_entity_name: e.target.value })}
                placeholder="e.g., Sunset Properties LLC"
                data-testid="new-le-name"
              />
            </div>
            <div>
              <Label>EIN</Label>
              <Input
                className="rounded-none mt-1 font-mono"
                value={newLeForm.ein}
                onChange={(e) => setNewLeForm({ ...newLeForm, ein: e.target.value })}
                placeholder="e.g., 12-3456789"
                data-testid="new-le-ein"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowAddLegalEntityDialog(false)}>
              Cancel
            </Button>
            <Button className="rounded-none" onClick={handleAddLegalEntity} data-testid="save-new-le-btn">
              Create & Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Locations;
